import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

/* Выпадающий список в дизайне платформы (замена нативному <select>).
   Список рисуем порталом в body с fixed-позиционированием: внутри модалок и
   панелей с overflow:hidden обычный absolute-поповер обрезался. */
export default function Dropdown({ value, onChange, options = [], placeholder = 'Выберите', disabled, size = 'md', style }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const popRef = useRef(null);

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const r = ref.current?.getBoundingClientRect();
      if (!r) return;
      const h = Math.min(264, options.length * 40 + 12);
      const below = r.bottom + 6;
      const top = below + h > window.innerHeight - 8 ? Math.max(8, r.top - h - 6) : below;
      setPos({ left: r.left, top, width: r.width, maxHeight: Math.min(264, window.innerHeight - top - 12) });
    };
    // Пересчитываем только на прокрутке СТРАНИЦЫ. Прокрутка внутри самого
    // списка тоже всплывала сюда: позиция обновлялась каждый кадр, список
    // перерисовывался и «сбрасывался» — колесо будто не работало.
    const onScroll = (e) => { if (popRef.current?.contains(e.target)) return; place(); };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', onScroll, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', onScroll, true); };
  }, [open, options.length]);

  // Показываем выбранный пункт сразу, не заставляя листать длинный список
  useEffect(() => {
    if (!open) return;
    const on = popRef.current?.querySelector('[data-active="true"]');
    if (on) popRef.current.scrollTop = on.offsetTop - popRef.current.clientHeight / 2 + on.clientHeight / 2;
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const sel = options.find(o => String(o.value) === String(value) && String(o.value) !== '');
  const sm = size === 'sm';

  return (
    <div ref={ref} style={{ position: 'relative', ...style }}>
      <button type="button" disabled={disabled} onClick={() => !disabled && setOpen(o => !o)}
        style={{ width: '100%', height: sm ? 36 : 46, padding: sm ? '0 34px 0 12px' : '0 40px 0 14px',
          borderRadius: sm ? 10 : 12, fontSize: sm ? 13 : 14, fontWeight: sm ? 600 : 400, fontFamily: 'inherit',
          border: `1.5px solid ${open ? 'var(--primary)' : 'var(--border)'}`, background: 'var(--card)',
          color: sel ? 'var(--text-1)' : 'var(--text-3)', textAlign: 'left', position: 'relative',
          cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1,
          boxShadow: open ? '0 0 0 4px rgba(124,77,255,0.14)' : 'none', transition: '.15s',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {sel ? sel.label : placeholder}
        <svg style={{ position: 'absolute', right: sm ? 11 : 14, top: '50%', transform: `translateY(-50%) rotate(${open ? 180 : 0}deg)`, transition: 'transform .2s' }}
          width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
      </button>

      {open && pos && createPortal(
        <div ref={popRef} className="dd-pop"
          style={{ position: 'fixed', left: pos.left, top: pos.top, width: pos.width, maxHeight: pos.maxHeight }}>
          {options.length === 0 ? (
            <div style={{ padding: '10px 12px', fontSize: 13, color: 'var(--text-3)' }}>Нет вариантов</div>
          ) : options.map(o => {
            const active = String(o.value) === String(value);
            return (
              <button key={o.value} type="button" data-active={active ? 'true' : undefined}
                className={`dd-opt${active ? ' on' : ''}`}
                onClick={() => { onChange(o.value); setOpen(false); }}>
                <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.label}</span>
                {active && <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}
